// Copyright ©️ 2026 eVolve MEP, LLC

import React, { useState } from 'react';

import {
  type WorkOrderItem,
  useEvolvePost,
  type WorkOrder,
  useEvolvePaginatedGet,
  type PartId,
  PART_TYPE_ASSEMBLY_ID,
} from '@evolvemep/api-ts';
import { Button, Text, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';

import { WrappedModal } from 'components/Mantine/Modals/WrappedModal';
import { FormPopover, useFormPopover } from 'components/Mantine/Popovers/FormPopover';
import { SearchableCheckList } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableCheckList';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { CatalogIntrospector } from 'modules/Shop/CatalogSetup/CatalogIntrospector';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import { getDisplayNameWO } from 'modules/Shop/WorkOrders/WorkOrderDetail/common';

type Props = {
  workOrder: WorkOrder;
  /** If not provided, will allow the user to select a work order item. */
  workOrderItem?: WorkOrderItem;
  opened: boolean;
  onClose: () => void;
  onBillOfProcessAdded: () => void;
};

type WorkOrderItemFormValues = {
  items: WorkOrderItem[];
};

const WorkOrderItemFields = ({
  workOrder,
  onSubmit,
}: Pick<Props, 'workOrder'> & {
  onSubmit: (workOrderItems: WorkOrderItem[]) => Promise<void>;
}) => {
  const paginatedGet = useEvolvePaginatedGet(`shop/workOrder/item`, {
    defaultConfig: { params: { workOrderId: workOrder.workOrderId } },
  });
  const form = useForm<WorkOrderItemFormValues>({
    initialValues: {
      items: [],
    },
    validate: {
      items: (items) => items.length === 0,
    },
  });

  const wrappedOnSubmit = async (values: typeof form.values) => {
    await onSubmit(values.items);
  };

  useFormPopover(form, wrappedOnSubmit);

  return (
    <>
      <Text size="sm" mb="xs">
        Select items to inherit the tasks from this assembly.
      </Text>
      <SearchableCheckList
        value={form.values.items}
        onChange={(items) => form.setFieldValue('items', items)}
        autoSelectWhenOnlyOneOption
        paginatedGet={paginatedGet}
        idKey="workOrderItemId"
        getItemLabel={(item) => item.name}
      />
    </>
  );
};

const WorkOrderItemSelect = ({
  workOrder,
  onSubmit,
  disabled,
}: Pick<Props, 'workOrder'> & {
  onSubmit: (workOrderItems: WorkOrderItem[]) => Promise<void>;
  disabled?: boolean;
}) => {
  const [opened, setOpened] = useState(false);

  return (
    <FormPopover
      opened={opened}
      onClose={() => setOpened(false)}
      target={
        <Button disabled={disabled} variant="light" onClick={() => setOpened(true)}>
          Inherit tasks
        </Button>
      }
    >
      <WorkOrderItemFields workOrder={workOrder} onSubmit={onSubmit} />
    </FormPopover>
  );
};

export const InheritBOPModal = ({ workOrder, workOrderItem, opened, onClose, onBillOfProcessAdded }: Props) => {
  const [selectedCatalog, setSelectedCatalog] = useLocalStorage('SELECTED_CATALOG', null);

  const { apiCall } = useEvolvePost('shop/workOrder/item/assemblyBop');

  const [saving, setSaving] = useState<PartId | null>(null);
  const onSubmit = async (partId: PartId, workOrderItems: WorkOrderItem[]) => {
    setSaving(partId);
    await apiCall({ workOrderItemIds: workOrderItems.map((item) => item.workOrderItemId), partId })
      .then(onBillOfProcessAdded)
      .then(() => {
        notifications.show({
          title: 'Successfully added',
          message: `Tasks added to ${hasLengthExactly(workOrderItems, 1) ? workOrderItems[0].name : `${workOrderItems.length} items`}.`,
          color: 'green',
        });
      })
      .finally(() => setSaving(null));
  };

  return (
    <WrappedModal
      title={isNotNil(workOrderItem) ? workOrderItem.name : workOrder.name}
      subtitle="Inherit assembly tasks"
      miniHeader={isNotNil(workOrderItem) ? getDisplayNameWO(workOrder) : workOrder.identifier}
      headerRightComponent={
        <CatalogSelect onCatalogSelected={setSelectedCatalog} selectedCatalog={selectedCatalog} w={250} />
      }
      opened={opened}
      onClose={onClose}
      hideModalButtons
      tall
      size="70vw"
      bodyProps={{ p: 0 }}
    >
      <CatalogIntrospector
        partCatalog={selectedCatalog}
        disablePart={(part) => part.partTypeId !== PART_TYPE_ASSEMBLY_ID}
      >
        {({ partId, partTypeId }) =>
          partTypeId !== PART_TYPE_ASSEMBLY_ID ? null : (
            <Stack justify="flex-end">
              {isNil(workOrderItem) ? (
                <WorkOrderItemSelect
                  workOrder={workOrder}
                  disabled={isNotNil(saving)}
                  onSubmit={async (items) => onSubmit(partId, items)}
                />
              ) : (
                <Button
                  loading={saving === partId}
                  disabled={isNotNil(saving)}
                  onClick={() => onSubmit(partId, [workOrderItem]).then(onClose)}
                  variant="light"
                >
                  Inherit assembly tasks
                </Button>
              )}
            </Stack>
          )
        }
      </CatalogIntrospector>
    </WrappedModal>
  );
};
